import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { rankingList } from '@/mocks/ranking';
import { formatHeat } from '@/utils/format';

type Period = 'today' | 'week' | 'month';

const tabs: { key: Period; label: string }[] = [
  { key: 'today', label: '今日热榜' },
  { key: 'week', label: '本周热榜' },
  { key: 'month', label: '本月热榜' },
];

const trendIcon: Record<string, { icon: string; className: string }> = {
  up: { icon: 'ri-arrow-up-line', className: 'text-primary-600' },
  down: { icon: 'ri-arrow-down-line', className: 'text-secondary-600' },
  same: { icon: 'ri-subtract-line', className: 'text-foreground-400' },
};

export default function HotRanking() {
  const [period, setPeriod] = useState<Period>('today');

  const list = useMemo(
    () => [...rankingList].sort((a, b) => b[period] - a[period]).slice(0, 8),
    [period],
  );

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
      <div className="flex flex-col gap-4 border-b border-background-200 pb-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-600">
            <i className="ri-fire-line text-lg" aria-hidden="true" />
          </span>
          <h3 className="m-0 text-lg text-foreground-950 md:text-xl">玩梗热力榜</h3>
        </div>
        <div className="inline-flex self-start rounded-full border border-background-200 bg-background-100 p-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setPeriod(tab.key)}
              aria-pressed={period === tab.key}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors md:px-4 md:text-sm ${
                period === tab.key
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <ol className="mt-2 divide-y divide-background-100">
        {list.map((item, index) => {
          const trend = trendIcon[item.trend] ?? trendIcon.same;
          const isTopThree = index < 3;
          return (
            <li key={item.id}>
              <Link
                to={`/scene/${item.id}`}
                className="flex items-center gap-3 py-3 transition-colors hover:bg-background-100 md:gap-4 md:px-2"
              >
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-heading text-base md:h-8 md:w-8 ${
                    isTopThree
                      ? 'bg-accent-500 text-secondary-950'
                      : 'bg-background-200 text-foreground-600'
                  }`}
                >
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-foreground-950 md:text-base">
                    {item.title}
                  </span>
                  <span className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">
                      {item.category}
                    </span>
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5 text-xs text-foreground-600 md:text-sm">
                  <i className={`${trend.icon} ${trend.className}`} aria-hidden="true" />
                  {formatHeat(item[period])}
                </span>
              </Link>
            </li>
          );
        })}
      </ol>

      <div className="mt-4 border-t border-background-200 pt-4">
        <Link
          to="/topics"
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-100 px-4 py-3 text-sm text-foreground-800 transition-colors hover:bg-background-200"
        >
          查看完整榜单与分类
          <i className="ri-arrow-right-line" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}